// src/pages/Admin/Adherents/ListingAdherentsAdmin.jsx

import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  FaFileExport,
  FaFilePdf,
  FaPlus,
  FaRedo,
  FaUsers,
  FaUserCheck,
  FaUserClock,
  FaUserSlash,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import {
  updateAdherent,
  deleteAdherent,
} from "../../../../services/adherentServices";

import generateAdherentPdf from "./AdherentPdfGenerator";
import useAdherentsAdmin from "./useAdherentsAdmin";

import {
  ALL_COLUMNS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  STORAGE_KEY,
} from "./columns.config";

import {
  STATUS_OPTIONS,
  countByStatus,
  countWithValidEmail,
  countWithoutGroup,
  getRoleId,
  getStoredUserInfo,
  getGroupPillClasses,
} from "./helpers";

import AdherentColumnPicker from "./components/AdherentColumnPicker";
import AdherentFiltersBar from "./components/AdherentFiltersBar";
import AdherentsTable from "./components/AdherentsTable";
import AdherentDrawer from "./components/AdherentDrawer";
import CreateAdherentDrawer from "./components/CreateAdherentDrawer";
import EditAdherentDrawer from "./components/EditAdherentDrawer";
import ManageAdherentGroupsDrawer from "./components/ManageAdherentGroupsDrawer";

function MiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {Number(value || 0).toLocaleString("fr-FR")}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Listing adhérents
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ListingAdherentsAdmin() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user || storedUser);

  const { state, setters, handlers } = useAdherentsAdmin();

  const {
    adherents,
    loading,
    seasons,
    activeSeason,
    groups,
    groupsAll,
    dojos,
    searchTerm,
    seasonFilter,
    statusFilter,
    communeFilter,
    selectedDojo,
    groupFilter,
    genreFilter,
    emailMode,
    communes,
    duplicateWarning,
    createdSortOrder,
    editingStatusId,
    visibleCount,
    scrollRef,
    filtered,
    visibleRows,
  } = state;

  const {
    setSearchTerm,
    setSeasonFilter,
    setStatusFilter,
    setCommuneFilter,
    setGroupFilter,
    setGenreFilter,
    setEmailMode,
    setEditingStatusId,
  } = setters;

  const {
    refreshAll,
    fetchInitialData,
    handleDojoChange,
    toggleCreatedSort,
    resetFilters,
    onScroll,
  } = handlers;

  const [drawer, setDrawer] = useState({
    type: null,
    adherent: null,
  });

  const [visibleColumnIds, setVisibleColumnIds] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed) && parsed.length) {
          return parsed.filter((id) =>
            ALL_COLUMNS.some((column) => column.id === id),
          );
        }
      }
    } catch {
      // ignore
    }

    return DEFAULT_VISIBLE_COLUMN_IDS;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumnIds));
    } catch {
      // ignore
    }
  }, [visibleColumnIds]);

  const selectAllColumns = () => {
    setVisibleColumnIds(ALL_COLUMNS.map((column) => column.id));
  };

  const selectNoColumns = () => {
    setVisibleColumnIds([]);
  };

  const resetDefaultColumns = () => {
    setVisibleColumnIds(DEFAULT_VISIBLE_COLUMN_IDS);
  };

  const stats = useMemo(() => {
    return {
      total: adherents.length,
      licencies: countByStatus(adherents, "Licencié"),
      inscrits: countByStatus(adherents, "Inscrit"),
      essais: countByStatus(adherents, "Cours d'essai"),
      sansGroupe: countWithoutGroup(adherents),
      emails: countWithValidEmail(adherents),
    };
  }, [adherents]);

  const getAge = useCallback(
    (dateNaissance) => {
      if (!dateNaissance) return "—";

      const birthDate = new Date(dateNaissance);

      if (Number.isNaN(birthDate.getTime())) return "—";

      const ref = activeSeason?.match(/^\d{4}-\d{4}$/)
        ? new Date(Number(activeSeason.split("-")[1]), 11, 31)
        : new Date();

      let age = ref.getFullYear() - birthDate.getFullYear();
      const monthDiff = ref.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && ref.getDate() < birthDate.getDate())
      ) {
        age -= 1;
      }

      return Number.isFinite(age) ? `${age} ans` : "—";
    },
    [activeSeason],
  );

  const updateAdherentInState = async (adherentId, patch) => {
    await updateAdherent(adherentId, patch);
    await refreshAll();
  };

  const handleStatusChange = async (adherentId, newStatus) => {
    try {
      await updateAdherentInState(adherentId, {
        statut_inscription: newStatus,
      });
      setEditingStatusId(null);
      toast.success("Statut mis à jour.");
    } catch (error) {
      console.error("Erreur mise à jour statut :", error);
      toast.error("Erreur lors de la mise à jour du statut.");
    }
  };

  const handleDeleteClick = async (adherentId) => {
    const target = adherents.find(
      (item) => String(item.id) === String(adherentId),
    );

    if (
      !window.confirm(
        `Supprimer l'adhérent ${target?.nom || ""} ${target?.prenom || ""} ?`,
      )
    ) {
      return;
    }

    try {
      await deleteAdherent(adherentId);
      toast.success("Adhérent supprimé.");
      await refreshAll();
    } catch (error) {
      console.error("Erreur suppression adhérent :", error);
      toast.error("Erreur lors de la suppression de l'adhérent.");
    }
  };

  const handleExportExcel = () => {
    const formatted = filtered.map((adherent) => ({
      ID: adherent.id,
      Nom: adherent.nom || "",
      Prénom: adherent.prenom || "",
      Statut: adherent.statut_inscription || "",
      Saison: adherent.saison_active || "",
      Email: adherent.email || "",
      "Email secondaire": adherent.email_secondaire || "",
      Téléphone: adherent.tel || "",
      "Téléphone secondaire": adherent.tel_secondaire || "",
      Genre: adherent.genre || "",
      Naissance: adherent.date_naissance
        ? new Date(adherent.date_naissance).toLocaleDateString("fr-FR")
        : "",
      Commune: adherent.commune || "",
      CP: adherent.cp || "",
      "Licence Judo": adherent.licence_number || "",
      "Licence JJB": adherent.licence_number_jjb || "",
      "Bénévole": adherent.is_benevole ? "Oui" : "Non",
      Groupes: (adherent.Groups || []).map((group) => group.nom).join(", "),
      Référents: (adherent.Users || [])
        .map((currentUser) =>
          `${currentUser.nom || ""} ${currentUser.prenom || ""}`.trim(),
        )
        .filter(Boolean)
        .join(", "),
      Notes: adherent.notes_adherent || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Adherents");

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "adherents_filtres.xlsx",
    );
  };

  const handlePrintPDF = () => {
    const doc = new jsPDF();

    const head = [["Nom", "Prénom", "Statut", "Email", "Naissance", "Groupes", "Licence Judo", "Licence JJB", "Bénévole"]];

    const body = filtered.map((adherent) => [
      adherent.nom || "",
      adherent.prenom || "",
      adherent.statut_inscription || "",
      adherent.email || "",
      adherent.date_naissance
        ? new Date(adherent.date_naissance).toLocaleDateString("fr-FR")
        : "",
      (adherent.Groups || []).map((group) => group.nom).join(", "),
      adherent.licence_number || "",
      adherent.licence_number_jjb || "",
      adherent.is_benevole ? "Oui" : "Non",
    ]);

    doc.text("Liste des adhérents - filtres appliqués", 14, 15);

    doc.autoTable({
      startY: 20,
      head,
      body,
      styles: {
        fontSize: 8,
      },
      headStyles: {
        fillColor: [1, 62, 142],
      },
    });

    doc.save("adherents_filtres.pdf");
  };

  const copyFilteredEmails = useCallback(async () => {
    const emails = Array.from(
      new Set(
        filtered
          .map((adherent) => String(adherent.email || "").trim())
          .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)),
      ),
    );

    if (!emails.length) {
      toast.info("Aucun email à copier pour les filtres actuels.");
      return;
    }

    const text = emails.join(";");

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Emails copiés (${emails.length}).`);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);

      toast.success(`Emails copiés (${emails.length}).`);
    }
  }, [filtered]);

  const ctx = {
    editingStatusId,
    setEditingStatusId,
    handleStatusChange,
    statusOptions: STATUS_OPTIONS,
    copyFilteredEmails,
    openGroupsDrawer: (adherent) => setDrawer({ type: "groups", adherent }),
    getGroupPillClasses,
    getAge,
  };

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <div className="relative z-40 overflow-visible rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>

                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Listing adhérents
                </h1>

                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Gérez les adhérents, leurs statuts, groupes, coordonnées,
                  exports et fiches PDF depuis une seule interface.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDrawer({ type: "create", adherent: null })}
                  className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <FaPlus />
                  Ajouter
                </button>

                <button
                  type="button"
                  onClick={handlePrintPDF}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaFilePdf />
                  PDF
                </button>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaFileExport />
                  Excel
                </button>

                <AdherentColumnPicker
                  visibleIds={visibleColumnIds}
                  setVisibleIds={setVisibleColumnIds}
                  onReset={resetDefaultColumns}
                  onSelectAll={selectAllColumns}
                  onSelectNone={selectNoColumns}
                />

                <button
                  type="button"
                  onClick={fetchInitialData}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaRedo className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>
              </div>
            </div>

            {duplicateWarning ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
                {duplicateWarning}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MiniStat
            icon={<FaUsers />}
            label="Adhérents"
            value={stats.total}
            helper="Total enregistré"
            accent="indigo"
          />

          <MiniStat
            icon={<FaUserCheck />}
            label="Licenciés"
            value={stats.licencies}
            helper="Statut Licencié"
            accent="emerald"
          />

          <MiniStat
            icon={<FaUserClock />}
            label="Inscrits"
            value={stats.inscrits}
            helper="En attente licence"
            accent="amber"
          />

          <MiniStat
            icon={<FaUserSlash />}
            label="Sans groupe"
            value={stats.sansGroupe}
            helper={`${stats.emails} email(s) valide(s)`}
            accent="rose"
          />
        </div>

        <div className="relative z-10 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <AdherentFiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            seasonFilter={seasonFilter}
            setSeasonFilter={setSeasonFilter}
            seasons={seasons}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            communeFilter={communeFilter}
            setCommuneFilter={setCommuneFilter}
            communes={communes}
            dojos={dojos}
            selectedDojo={selectedDojo}
            handleDojoChange={handleDojoChange}
            groups={groups}
            groupFilter={groupFilter}
            setGroupFilter={setGroupFilter}
            genreFilter={genreFilter}
            setGenreFilter={setGenreFilter}
            emailMode={emailMode}
            setEmailMode={setEmailMode}
            filteredCount={filtered.length}
            resetFilters={resetFilters}
          />

          <AdherentsTable
            rows={visibleRows}
            totalCount={filtered.length}
            visibleCount={visibleCount}
            loading={loading}
            scrollRef={scrollRef}
            onScroll={onScroll}
            visibleColumnIds={visibleColumnIds}
            createdSortOrder={createdSortOrder}
            toggleCreatedSort={toggleCreatedSort}
            ctx={ctx}
            onEdit={(adherent) => setDrawer({ type: "edit", adherent })}
            onDelete={handleDeleteClick}
            onPrint={generateAdherentPdf}
            onGroups={(adherent) => setDrawer({ type: "groups", adherent })}
          />
        </div>
      </div>

      <CreateAdherentDrawer
        isOpen={drawer.type === "create"}
        onClose={() => setDrawer({ type: null, adherent: null })}
        refreshList={refreshAll}
      />

      <EditAdherentDrawer
        isOpen={drawer.type === "edit"}
        onClose={() => setDrawer({ type: null, adherent: null })}
        adherent={drawer.adherent}
        refreshList={refreshAll}
      />

      <ManageAdherentGroupsDrawer
        isOpen={drawer.type === "groups"}
        onClose={() => setDrawer({ type: null, adherent: null })}
        adherent={drawer.adherent}
        groups={groupsAll}
        refreshList={refreshAll}
      />

      <ToastContainer />
    </section>
  );
}
